/**
 * Reusable "coming soon" shell for the 5 resources sub-pages.
 * Consistent chrome with newsletter signup + preview list.
 */
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Mail, ChevronRight, Rss } from "lucide-react";
import { MarketingHeader, MarketingFooter } from "../MarketingChrome";

export default function ResourceShellTemplate({
  slug,
  eyebrow,
  title,
  titleAccent,
  subtitle,
  icon: Icon,
  previewItems = [],
  previewLabel = "Coming up",
}) {
  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid={`resource-shell-${slug}`}>
      <MarketingHeader />

      {/* HERO */}
      <section
        data-testid="resource-hero"
        className="relative overflow-hidden bg-[#0B1F3A] text-white"
      >
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(5,150,105,0.22),transparent_60%)]"></div>
        <div className="relative max-w-7xl mx-auto px-6 py-20 md:py-24">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-1 rounded-full bg-white/10 border border-white/20 px-3 py-1 text-xs font-semibold">
              <Sparkles className="w-3 h-3 text-emerald-400" />
              {eyebrow}
            </div>
            <h1 data-testid="resource-hero-headline" className="mt-6 text-4xl md:text-6xl font-bold tracking-tight leading-[1.05]">
              {title}
              {titleAccent && (
                <>
                  <br />
                  <span className="text-emerald-400">{titleAccent}</span>
                </>
              )}
            </h1>
            <p className="mt-6 text-lg text-slate-300 max-w-2xl">{subtitle}</p>
          </div>
        </div>
      </section>

      {/* CONTENT */}
      <section className="max-w-7xl mx-auto px-6 py-16 md:py-24 grid md:grid-cols-3 gap-8">
        <div className="md:col-span-2">
          <div className="text-xs uppercase tracking-[0.2em] text-emerald-500 font-semibold">
            {previewLabel}
          </div>
          <h2 className="mt-2 text-2xl md:text-3xl font-bold">
            {previewItems.length > 0 ? "Fresh content dropping soon." : "Content publishing schedule."}
          </h2>
          <p className="mt-3 text-slate-600 max-w-2xl">
            We&rsquo;re shipping the platform&rsquo;s substrate this quarter — this stream goes live alongside it. Subscribe to get notified the moment the first piece publishes.
          </p>

          {previewItems.length > 0 && (
            <ul className="mt-8 divide-y divide-slate-200 rounded-xl border border-slate-200 bg-white overflow-hidden">
              {previewItems.map((item, i) => (
                <li key={item.title} className="p-5 flex items-start gap-4">
                  {Icon && (
                    <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-4 h-4 text-emerald-600" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">
                      {item.meta ?? `Preview · #${String(i + 1).padStart(2, "0")}`}
                    </div>
                    <div className="mt-1 font-semibold text-slate-900">{item.title}</div>
                    {item.desc && <div className="mt-1 text-sm text-slate-600">{item.desc}</div>}
                  </div>
                  <div className="text-xs text-slate-400 flex-shrink-0">Soon</div>
                </li>
              ))}
            </ul>
          )}

          <div className="mt-8">
            <Link
              to="/resources"
              className="inline-flex items-center gap-1 text-emerald-600 font-semibold hover:underline"
            >
              <ChevronRight className="w-4 h-4 rotate-180" />
              Back to Resources
            </Link>
          </div>
        </div>

        {/* SIDEBAR — subscribe */}
        <aside className="space-y-4">
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6">
            <Mail className="w-6 h-6 text-emerald-700" />
            <div className="mt-3 font-bold text-emerald-900">Get notified</div>
            <p className="mt-1 text-sm text-emerald-800">
              We&rsquo;ll email you when the first piece in this stream goes live.
            </p>
            <Link
              to="/contact"
              data-testid="resource-notify-cta"
              className="mt-4 inline-flex items-center gap-2 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-white font-semibold px-4 py-2 text-sm"
            >
              Notify me <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="rounded-2xl border border-slate-200 p-6">
            <Rss className="w-6 h-6 text-emerald-600" />
            <div className="mt-3 font-bold">RSS &amp; feeds</div>
            <p className="mt-1 text-sm text-slate-600">
              Every stream will publish an RSS/Atom feed you can pipe into your reader of choice.
            </p>
          </div>

          <div className="rounded-2xl border border-slate-200 p-6">
            <Sparkles className="w-6 h-6 text-emerald-600" />
            <div className="mt-3 font-bold">Explore now</div>
            <p className="mt-1 text-sm text-slate-600">
              While this stream fills up, the product itself is the deepest doc — take the tour.
            </p>
            <Link
              to="/auth?mode=signup"
              className="mt-3 inline-flex items-center gap-1 text-emerald-600 font-semibold text-sm hover:underline"
            >
              Start free trial <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </aside>
      </section>

      <MarketingFooter />
    </div>
  );
}
